import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Section, SectionHeading } from "@/components/site/Section";
import { SeoGenerator } from "@/components/site/SeoGenerator";
import { getReviewAdmin, saveReviewWidgets } from "@/lib/reviewSettings.functions";

export const Route = createFileRoute("/beheer")({
  head: () => ({
    meta: [
      { title: "Beheer reviews | Vakkundig Verhuizingen & Transport" },
      { name: "description", content: "Beheerinstellingen voor automatische reviewwidgets." },
      { property: "og:title", content: "Beheer reviews | Vakkundig" },
      { property: "og:description", content: "Beheerinstellingen voor reviewwidgets." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  loader: () => getReviewAdmin(),
  component: BeheerPage,
});

function BeheerPage() {
  const data = Route.useLoaderData();
  const [trustoo, setTrustoo] = useState(data.status === "ok" ? data.trustoo : "");
  const [google, setGoogle] = useState(data.status === "ok" ? data.google : "");
  const [saving, setSaving] = useState(false);

  if (data.status !== "ok") {
    return (
      <Section>
        <SectionHeading as="h1" eyebrow="Beheer" title="Geen toegang" intro={
          data.status === "signed_out"
            ? "Open deze pagina terwijl je bent ingelogd bij Lovable."
            : "Deze beheerpagina is gekoppeld aan een ander account."
        } />
      </Section>
    );
  }

  async function save() {
    setSaving(true);
    try {
      await saveReviewWidgets({ data: { trustoo, google } });
      toast.success("Opgeslagen. De reviews staan nu op de reviewpagina.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  }

  const field = "mt-2 h-40 w-full rounded-md border border-input bg-background p-3 font-mono text-xs";
  return (
    <Section>
      <SectionHeading as="h1" eyebrow="Beheer" title="Reviews automatisch tonen"
        intro="Plak hier de widgetcode. Nieuwe reviews verschijnen dan vanzelf op de reviewpagina." />
      {!data.claimed ? (
        <p className="mb-6 rounded-md border border-brand bg-accent p-4 text-sm">
          Bij de eerste keer opslaan wordt deze pagina vastgezet op jouw account. Daarna kan niemand anders hem wijzigen.
        </p>
      ) : null}
      <div className="grid gap-8">
        <label className="block">
          <span className="font-bold text-secondary">Trustoo review-widget</span>
          <span className="block text-sm text-muted-foreground">Te vinden in je Trustoo-account onder Widgets.</span>
          <textarea className={field} value={trustoo} onChange={(e) => setTrustoo(e.target.value)} placeholder="<script ...></script>" />
        </label>
        <label className="block">
          <span className="font-bold text-secondary">Google-reviewkoppeling (bijv. Elfsight of Trustmary)</span>
          <span className="block text-sm text-muted-foreground">Plak de insluitcode van je Google-reviewwidget.</span>
          <textarea className={field} value={google} onChange={(e) => setGoogle(e.target.value)} placeholder="<div class=...></div><script ...></script>" />
        </label>
        <div className="flex items-center gap-4">
          <button onClick={save} disabled={saving}
            className="rounded-md bg-brand px-5 py-2.5 text-sm font-bold text-brand-foreground hover:bg-brand-dark disabled:opacity-60">
            {saving ? "Opslaan..." : "Opslaan"}
          </button>
          {data.updatedAt ? (
            <span className="text-xs text-muted-foreground">
              Laatst bijgewerkt: {new Date(data.updatedAt).toLocaleString("nl-NL")}
            </span>
          ) : null}
        </div>
      </div>
      <SeoGenerator />
    </Section>
  );
}
